/**
 * CityGraph — the data spine for settlement growth (W2a).
 *
 * A region's street network, parcels, and growth history live in FLAT typed
 * arrays inside a single ArrayBuffer, so a generated region can be:
 *   - byte-compared against a fresh regeneration (determinism currency), and
 *   - transferred zero-copy to a worker (W2b).
 *
 * Region-local coordinates use a tangent CHART pinned at the cell center:
 *   u = radius * (t1 . dir),  v = radius * (t2 . dir)      [dirToLocal]
 *   dir = normalize(center + (u/radius)*t1 + (v/radius)*t2) [localToDir]
 * The chart carries coordinates only — HEIGHTS are always sampled at the real
 * direction, never derived from the chart. Round-trip error is sub-millimeter
 * for offsets up to ~1.2 km on an 8 km frame (see worldgen-city.test.mts).
 *
 * Determinism note: every float here is stored/loaded through a DataView, whose
 * IEEE-754 byte layout ECMA-262 fully specifies, so serialization is bit-exact
 * on every conforming engine. No engine-approximated Math.* intrinsic is used
 * (pmath.hypot handles the one norm); the pmath-ban gate covers this file.
 */
import * as pmath from "../pmath.js";
import { type CellId, cellCenterDir, packCell, unpackCell } from "./cells.js";
import { tangentBasis } from "./fields.js";

// ── versioning + kind enums (public, binding) ──────────────────────────────

// Bump on ANY change that shifts the growth digest — format OR content — so
// caches (serialized CityGraph buffers) invalidate. v2: water discipline for
// streets/parcels (densify/emitParcels reject wet endpoints/corners). v3
// (W2b-2.5): parcel road-exclusion (emitParcels rejects lots on foreign road
// ribbons) + arterial meander (0xa77e heading perturbation, ARTERIAL_BLEND
// 0.45→0.25) — both shift generated content, so the cache key must move with it.
// v4 (W2b-2.6 generation discipline): arterials trace ONLY in seed-bearing cells
// and only from the <=2 seed-nearest ports; a hard road grade cap terminates
// steep traces (ROAD_MAX_GRADE); dead-end chains are pruned to a fixpoint before
// parcels; parcels emit only within PARCEL_SEED_RADIUS_M of a seed. All shift
// generated content, so the cache key moves again.
// v5 (W2b-2.7 hamlet hotfix): (a) UTILITY pruning after emitParcels deletes
// STREET/STAIR edges farther than PARCEL_SERVICE_M from every parcel centroid,
// then re-prunes dead ends — so streets exist only where dwellings are; (b)
// densify only closes a loop when the pair is >= DENSIFY_LOOP_MIN_HOPS apart on
// the live graph (kills second-neighbour ladders); (c) arterials trace only when
// the cell's max seed sizeClass >= 1 (hamlets get local streets, no trunk roads).
// All three shift generated content, so the cache key moves again.
// v6 (W2c-1 tier core, Task 1): settlementSeeds are now dealt from the per-planet
// TierRegistry quota deck (SettlementSeed.sizeClass → tier 0..5), so WHICH cells
// bear settlements and at WHAT size both change planet-wide — the growth digest
// shifts and cached buffers must invalidate. (Bumped in Task 1 because the seed
// change alone already alters graph content.)
// v7 (W2c-1 tier core, Task 2): required-building placement writes meaningful
// USE_* codes into every Parcel.use (was always 0), the header carries the
// region's PRIMARY-settlement tier/population/deviation metadata (three extra
// header uint32s), and TIER_PARAMS replaces SIZE_PARAMS so surviving building
// counts + parcel radii are tier-scaled. Both the header FORMAT and the graph
// CONTENT change, so the cache key moves again and the growth digest re-records
// (authorized once, this phase). Task 1's v6 bytes only ever shipped to tests
// (caches are version-keyed), so bumping to 7 costs nothing and keeps the format
// clean now that placement + metadata land under one version.
// v8 (W2d-1 Task 2): A* PRIMARY roads + hamlet through-roads + waystation paths
// REPLACE the contour-streamline arterials. VILLAGE+ cells route primaries between
// hashed anchors (emitted as EDGE_ARTERIAL edges — same enum value, new CONCEPT);
// hamlets get one A* through-road + cottage stubs; waystations gain building-to-
// building paths; the SC interiors now graft onto primary nodes. Node ORDER,
// edge geometry, and parcel placement all shift, so the growth digest re-records
// (authorized once, this phase) and cached buffers invalidate. Format is
// otherwise unchanged (no header/section layout change). NOTE: the v3/v4
// changelog entries above are HISTORICAL — their arterial-era symbols
// (0xa77e, ARTERIAL_BLEND, traceArterials, ROAD_MAX_GRADE) were deleted when v8
// retired the contour-streamline tracer, so grepping for them will find nothing.
// v9 (W2d-2 Task 3): ERA'D TENSOR-FIELD secondary street growth under the GLOBAL
// PLANARITY INVARIANT (streetgrow.ts) REPLACES the space-colonization skeleton +
// densify for VILLAGE+ cells. Secondaries are era-banded EDGE_STREET edges logged
// LOG_EXTEND; every insertion snap/splits against the shared planar net (primaries
// included) so no two edges cross without a junction. Node order, edge geometry,
// epoch banding, and parcel placement all shift, so the growth digest re-records
// (authorized once, this phase) and cached buffers invalidate. Format is otherwise
// unchanged. The SC-era symbols (growSettlement, densify, GRADE_STAIR/TUNNEL,
// DISTRICT_PARAMS, LOG_TYPESWITCH edges) are retired — grepping finds nothing.
// v10 (R1 road readjustment, 2026-07-09; refined 2026-07-10 R1 review): primaries are
// now CONNECTED (per-cell union-find linking of disconnected primary components → one
// network per cell; pruneUtility gains a bridge-edge exemption so a mid-chain street
// whose removal would disconnect its component is never pruned), CROSS-CELL PLANAR at
// borders, and DE-QUANTIZED / gently curved. The ACTUAL shipped mechanisms (the spec's
// hashed approach-CORRIDOR cost wall was implemented, MEASURED counterproductive, and
// DROPPED — it distorted A* routing and ADDED border crossings):
//   • cross-cell planarity = a hard router chart BOUND (own cell half-extent + a 12 m
//     collar, region-independent) PLUS an EXACT per-cell CONFINEMENT (the router
//     rejects any lattice node/step whose world direction falls in a NEIGHBOUR cell —
//     dirToCell, the true curved border, not the axis-aligned collar; the shared goal
//     PORT is exempt), so adjacent cells' primaries meet only at shared border ports.
//     De-quant is likewise confined in-cell (a jitter/bend/Chaikin move that would leave
//     the cell is rejected). See makeInCell / RouteOpts.inCell / dequantizePrimary.
//   • de-quant = per-node lattice jitter ≤6 m (perpendicular, sign alternating by node
//     parity), a 120 m straight-run bend cap, and Chaikin ×1 (NOT ×2 — ×2 reintroduces
//     intra-cell crossings; see CHAIKIN_ROUNDS). Node order, edge geometry (more/shorter
//     primary edges), and parcel placement all shift, so the growth digest re-records
//     (authorized once, this phase) and cached buffers invalidate. Format is otherwise
//     unchanged (edges are still straight 2-node segments; the curve is carried as
//     more, shorter, degree-2 chain edges — no per-edge control-point channel).
//   • hamlet coverage (R1 Task 1.5): hamlet through-roads/legs/links now get the SAME
//     planarity discipline + de-quant ladder (a per-cell PlanarNet, repair, then jitter→
//     run-cap→Chaikin) as VILLAGE+ primaries — the T1 fix wave had frozen them on the raw
//     45° lattice (self-crossing risk); a post-de-quant repair (sparse hamlet cells only)
//     keeps them self-planar. Same salts, so the growth digest shift stays in this epoch.
//   • perimeter CITY walls removed (R1 D6, 2026-07-10): the wall generator, its tier
//     gates, and the collision/render/salt hooks are deleted (user: "we don't need walls
//     around cities"). Walls were NEVER a CityGraph serialization channel — they were
//     recomputed structures-side from settlementSeeds + the graph — so the SERIALIZED
//     FORMAT is byte-unchanged by their removal and VERSION STAYS 10 (this epoch is still
//     open). Retaining walls (terrain-holding, road/parcel-derived) are unaffected.
//     Growth digest re-records within the R1 epoch; worldgen/contract/rivers HOLD.
// v11 (W2d-3 blocks/lots/width classes, 2026-07-11): FORMAT + CONTENT change.
//   • FORMAT: every edge gains a WIDTH CLASS (u8, CLASS_*) and a resolved WIDTH in
//     metres (f32) — two new sections serialized after edgeEpoch (spec §"Widths
//     become per-edge data … CityGraph gains an edge width/class channel (format
//     bump); carve ROAD_W becomes per-edge"). Classes: PRIMARY (A* trunk,
//     EDGE_ARTERIAL/BRIDGE), SECONDARY (tensor streets), TERTIARY (lanes at
//     degree-1 leaves + STAIR/TERRACE steep connectors), ALLEY (block-subdivision
//     alley cuts, hamlet driveway stubs, waystation paths). Widths come from the
//     spec §"Street widths" table by class × tier (ranged city/metro primary
//     widths hash-rolled once per cell, salt 0x77d1) with the spec era multipliers
//     on city/metro SECONDARY edges (epoch percentile < ⅓ → ×0.75, > ⅔ → ×1.15).
//   • CONTENT: PlanarNet gained a duplicate-edge guard (the A* reuse discount had
//     every route RE-EMIT shared trunk segments — up to ~100 exact duplicate edges
//     per city cell, breaking planar FACE tracing); blocks (planar faces) are
//     subdivided into building LOTS that REPLACE the strip parcels for VILLAGE+
//     cells (strips remain only as fallback on street sides not facing a lotted
//     block); alley edges are inserted into blocks. Node order, edge geometry,
//     parcel geometry/count, and placement all shift, so the growth digest
//     re-records (authorized once, this phase) and cached buffers invalidate.
// v12 (W2d-4 desire lines/plazas, 2026-07-11): CONTENT-only change (format holds).
//   The promotion pass (growth.ts promoteStreets, spec §"Desire-line / promotion
//   pass") re-ranks every VILLAGE+ street graph: desire-crossed ALLEY/TERTIARY
//   footpaths promote a class, the top-K betweenness SECONDARY runs widen to
//   MAIN-STREET width (tier primary × spec multiplier) — both ride the v11
//   edgeClass/edgeWidth channels — and the plaza stage emits open plaza parcels
//   at top-betweenness junctions with plaza-first required-building placement.
//   Widths/classes/parcels/placement all shift, so the growth digest re-records
//   (authorized once, this phase) and cached buffers invalidate.
// v13 (post-W2d-4 cliff fix, 2026-07-11): CONTENT-only change — roads no longer
//   climb cliffs. The A* router forbids steps steeper than ROAD_HARD_GRADE
//   (tan 30°) outright (was ×4 soft cost; ungated RETRY preserves the R1
//   one-component connectivity when a wall-abiding route doesn't exist), and
//   the secondary grower rejects candidate segments steeper than tan 25°.
//   Primary/secondary geometry shifts, so the growth digest re-records
//   (authorized, fix wave) and cached buffers invalidate. (The paint-side cliff
//   gate — no road paint where the carve cliff-clamps k — is render-only.)
// v14 (R3, 2026-07-11): CONTENT-only changes (format holds).
//   • LANDMARKS: the tier flagship (temple/palace/nexus) claims the parcel
//     nearest its hashed landmarkSite (salt 0x1a4d) before other placement; the
//     plaza tie-breaks toward it; parcels' uses shift.
//   • METRO SPRAWL: cells edge-adjacent (same face) to a dealt METRO seed grow a
//     TOWN-budget sprawl pseudo-seed toward the metro (salt 0x5b4a; registry-
//     driven, region-independent) — the metro cluster finally carries the spec
//     300–600 building count. Node/edge/parcel content shifts in those cells,
//     so the growth digest re-records (authorized, R3 epoch) and caches
//     invalidate.
// v16 (steep-site + street d_sep braid fix, 2026-07-11): CONTENT-only. Two
//   mechanisms the v15 primary fix never touched (user-located live: 3:-1:2
//   Voxes cell (5,5,7), a tier-4 city on a mountain flank with 68 parcels):
//   (1) the tier deal gated tier ≥ VILLAGE on measured BUILDABILITY —
//   areaSuitability's 200 m macro slope misses building-scale ridge detail, so
//   sites >40% above the street grade wall ranked near-top, then the grower
//   could only trace braided contour strands (steepFracAt probe, skip-down to
//   hamlet); (2) the tensor grower gained the classic d_sep rule — no street
//   segment may run parallel (|cos| ≥ 0.9) within 12 m lateral of a street it
//   doesn't junction with (PlanarNet.parallelWithin), which also kills pointy
//   hairpin loop-backs. Settlements re-site + all street fabric shifts.
// v15 (R3 braid fix, 2026-07-11): CONTENT-only — parallel primaries MERGE. The
//   router's ×0.4 reuse discount fired within 20 m (one full lattice step), so a
//   route riding the ADJACENT lattice line beside a trunk earned the discount
//   without joining it — multi-anchor cells braided (user screenshot; measured
//   10–15 redundant parallel primary pairs/cell). The discount now fires only
//   ON the road (≤ 7 m) and the 7–26 m beside-road corridor pays ×1.6 — routes
//   join trunks or keep their distance (measured braids → ~0). Primary geometry
//   shifts; growth digest re-records (authorized, R3 wave); caches invalidate.
// v17 (2026-07-28): interiors scale-up — parcel strips 16→20 m deep / 12–19 m
// wide, block lot areas ×1.5. Cached v16 graphs carry the old parcel geometry
// and must regenerate.
// v18: hydrology v2 carves waterfall lips/pools. Regenerate settlement buffers
// against the new ground; adopting a v17 road bed could bridge an obsolete slope.
// v19: hydrology v3 narrows waterfall throats and resolves rocky bank terraces.
// v20: one authoritative administration site and central ward per real seed.
// v21: hydrology v4 keeps gorge transitions below the river surface.
// v22: rivers follow full terrain with capped incision; lake routing precedes channels.
// v23: reference-shaped civic halls, raised access stairs and faceted stone-cradle wards.
// v24: sampled road hazards/bridges and strictly contained building footprints.
// v25: compound civic halls with larger reserved sites and connected room interiors.
// v26: local hills and shelves change native ground, drainage and settlement
// suitability. Regenerate road/parcel levels against the new shared terrain.
// v27: asymmetric river benches change shared bank support and parcel levels.
// v28: regenerate cached roads/parcels against coastal glacial terrain.
// v29: volcanic cones change support; roads reject active lava channels.
// v30: regenerate road/parcel levels and suitability against mountain masses.
// v31: regional ranges/valleys change native support and settlement suitability.
// v32: coastal beaches/headlands change native ground and settlement suitability.
// v33: regenerate road/parcel support against classified shores and estuaries.
// v34: regenerate coastal support/suitability against solid offshore stacks.
// v35: regenerate road/parcel support against plateau caps and escarpments.
// v36: regenerate road/parcel support against finite native dune provinces.
// v37: regenerate support and routes around native sandstone butte groups.
// v38: regenerate roads and parcel support around native canyon incision.
// v42: regenerate bank roads/parcels against continuous river cross-sections.
// v43: regenerate routes/parcels against resolved native waterfall cliffs.
// v44: regenerate route/parcels against attached waterfall-bank shelves.
// v45: rebuild settlement support against finite cascade formations.
export const CITYGRAPH_VERSION = 45;

export const NODE_JUNCTION = 0, NODE_SEED = 1, NODE_PORT = 2;
// EDGE_ARTERIAL is the value the W2d-1 PRIMARY roads carry — the enum value is
// KEPT for CityGraph format stability, but the CONCEPT is now "primary" (A*
// trunk-and-branch), not the retired contour-streamline arterial. Width classes
// (spec TERTIARY/ALLEY for hamlet stubs / waystation paths) arrive W2d-3; until
// then those surface links are EDGE_STREET.
export const EDGE_ARTERIAL = 0, EDGE_STREET = 1, EDGE_STAIR = 2,
             EDGE_TUNNEL = 3, EDGE_TERRACE = 4, EDGE_BRIDGE = 5;
export const LOG_EXTEND = 0, LOG_TYPESWITCH = 1, LOG_LOOP = 2, LOG_PARCEL = 3;

// ── edge WIDTH CLASSES (W2d-3, spec §"Street widths") ───────────────────────
//
// The per-edge width/class channel (v11). CLASS is the street-hierarchy rank
// (what the edge IS); WIDTH is the resolved carve/render width in metres (what
// the class × settlement tier × era multipliers yield — resolved at generation
// so every consumer reads one number). CLASS_UNSET is the builder default for
// edges created before the classify pass; it must never survive into a built
// graph (classifyEdges fills it — oracled).
export const CLASS_PRIMARY = 0, CLASS_SECONDARY = 1, CLASS_TERTIARY = 2, CLASS_ALLEY = 3;
export const CLASS_UNSET = 255;

// ── parcel USE_* codes (W2c-1 Task 2, the "required buildings → Parcel.use" map)
//
// Every entry in the settlement-tier spec's required + optional lists gets a code
// here; TIER_REQUIREMENTS / OPTIONAL_POOLS (growth.ts) reference these, and
// assignUses stamps the winning code into Parcel.use near the seed. USE_DWELLING
// (0) is the default/remainder (was the only value pre-Task-2). USE_MARKET_SQ is
// an OPEN square — assignUses reserves the parcel but structures.ts stamps NO
// building on it (the open-space market of spec §Village; stalls arrive W2c-2).
// USE_PRODUCTION is the abstract "one production building tied to local biome"
// slot; the concrete farm/fishery/mine/lumber SUBTYPE is a pure function of the
// seed biome (see productionType) resolved by later dress passes, not stored here.
// Codes are append-only (never renumber — they serialize into the graph).
export const
  USE_DWELLING = 0, USE_DEPOT = 1, USE_BUNKHOUSE = 2, USE_LANDING_PAD = 3,
  USE_WELL = 4, USE_HALL = 5, USE_PRODUCTION = 6, USE_INN = 7, USE_MARKET_SQ = 8,
  USE_CHAPEL = 9, USE_WORKSHOP = 10, USE_HEADMAN = 11, USE_WAREHOUSE = 12,
  USE_MARKET_HALL = 13, USE_TEMPLE = 14, USE_GUILDHALL = 15, USE_SMITHY = 16,
  USE_GARRISON = 17, USE_ADMIN = 18, USE_LOOM_GATE = 19, USE_PALACE = 20,
  USE_ACADEMY = 21, USE_HARBOR = 22, USE_COURTHOUSE = 23, USE_MONUMENT = 24,
  USE_STARPORT = 25, USE_EMBASSY = 26, USE_PRECURSOR = 27, USE_BASILICA = 28,
  USE_LOOM_NEXUS = 29,
  // optional-pool codes (rolled, not guaranteed)
  USE_SHRINE = 30, USE_MILL = 31, USE_STABLE = 32, USE_APOTHECARY = 33,
  USE_BATHHOUSE = 34, USE_THEATER = 35, USE_ARENA = 36, USE_DOCKYARD = 37,
  USE_PRISON = 38, USE_WATCHTOWER = 39, USE_FUEL_SHED = 40;
/** One past the highest USE_* code (enum size; append-only). */
export const USE_COUNT = 41;
// EPOCH NOTE (for W4 age ranking): epoch 0 is shared by ALL arterial-stage edges
// (arterials never log, so they keep epoch 0) AND by the first LOGGED edge — the
// two are disambiguated by the KIND partition (arterials/bridges are unlogged;
// logged edges are STREET/STAIR/TUNNEL/TERRACE/loop). Cross-settlement epochs are
// SEQUENTIAL, not per-settlement: settlement k=1's history is entirely "younger"
// than k=0's (its epoch counter continues from k=0's final log length), so W4
// should rank construction age WITHIN a settlement, not by raw epoch across them.

// ── types ──────────────────────────────────────────────────────────────────

export interface RegionFrame {
  /** Cell center unit dir. */ cx: number; cy: number; cz: number;
  t1: [number, number, number]; t2: [number, number, number];
  radius: number; // planet radius (m) — the chart scale
}

export interface Parcel {
  poly: Float64Array;      // [u0,v0, u1,v1, ...] region-local meters, CCW
  use: number;             // 0 dwelling for W2a (roster semantics arrive in W4)
  epoch: number;           // growth step of the street edge it fronts
  level: number;           // pad level, amplitude units, frozen at creation
  retaining: boolean;      // any corner grade exceeded the retaining threshold
}

export interface CivicPlacement {parcel:number;u:number;v:number;yaw:number}
export interface SettlementCivicSite {seedK:number;tier:number;admin:CivicPlacement;ward:CivicPlacement}
export interface CityGraph {
  regionId: CellId;
  frame: RegionFrame;
  nodeU: Float64Array; nodeV: Float64Array; nodeKind: Uint8Array;
  edgeA: Uint32Array; edgeB: Uint32Array; edgeKind: Uint8Array; edgeEpoch: Uint32Array;
  /** Per-edge width class (CLASS_*, v11) — the street-hierarchy rank. */
  edgeClass: Uint8Array;
  /** Per-edge resolved width, metres (v11) — carve/render/exclusion read this. */
  edgeWidth: Float32Array;
  parcels: Parcel[];
  logKind: Uint8Array; logRef: Uint32Array; logSlope: Float32Array; // step = index
  version: number;
  /**
   * Region PRIMARY-settlement metadata (W2c-1 Task 2 header extension). The
   * primary is the cell's highest-tier dealt seed (ties → lowest k); these drive
   * W4 roster sizing + census. `tier` is -1 for a settlement-less cell (empty /
   * port-only / pruned-to-nothing); `population` (0 when tier < 0) is hash-rolled
   * within the primary tier's spec range; `deviation` (0 = none) is the primary
   * seed's rule-breaker id. Serialized in the header (tier stored as tier+1 so 0
   * encodes "none").
   */
  tier: number; population: number; deviation: number;
  /** Real settlements only. Sprawl districts share their parent city's ward. */
  civics?: SettlementCivicSite[];
}

// ── region frame + tangent chart ───────────────────────────────────────────

/** Build the region-local tangent chart at the cell center (spec §5). */
export function regionFrame(radius: number, cell: CellId): RegionFrame {
  const c: number[] = [0, 0, 0];
  cellCenterDir(radius, cell, c);
  const t1: [number, number, number] = [0, 0, 0];
  const t2: [number, number, number] = [0, 0, 0];
  tangentBasis(c[0], c[1], c[2], t1, t2);
  return { cx: c[0], cy: c[1], cz: c[2], t1, t2, radius };
}

/** Project a unit direction into region-local meters (u,v). */
export function dirToLocal(f: RegionFrame, dx: number, dy: number, dz: number, out: [number, number]): void {
  out[0] = f.radius * (f.t1[0] * dx + f.t1[1] * dy + f.t1[2] * dz);
  out[1] = f.radius * (f.t2[0] * dx + f.t2[1] * dy + f.t2[2] * dz);
}

/**
 * Invert the chart: region-local meters (u,v) → unit direction.
 *
 * This is the EXACT inverse of the orthographic forward map: (u/R, v/R) are the
 * dir components along t1,t2, so the along-center component is recovered as
 * sqrt(1 - p² - q²) (positive hemisphere — regions never wrap past the horizon).
 * The result is already unit length, so no renormalization is needed.
 *
 * DEVIATION FROM BRIEF (documented): the brief sketched
 * `normalize(center + (u/R)·t1 + (v/R)·t2)`. That is only a first-order
 * approximation of the inverse; measured, it drifts ~13 m at a 1.2 km offset on
 * an 8 km frame — which BLOWS the brief's own 1 mm round-trip oracle (planet
 * radii in this game are ~8–21 km, so 1.2 km offsets are real). The true inverse
 * below costs one extra sqrt (a spec-exact op, allowed by the pmath ban) and
 * round-trips to machine precision, so the oracle passes "comfortably" as stated.
 */
export function localToDir(f: RegionFrame, u: number, v: number, out: number[] | Float64Array): void {
  const p = u / f.radius;
  const q = v / f.radius;
  const cc = Math.sqrt(Math.max(0, 1 - p * p - q * q));
  out[0] = cc * f.cx + p * f.t1[0] + q * f.t2[0];
  out[1] = cc * f.cy + p * f.t1[1] + q * f.t2[1];
  out[2] = cc * f.cz + p * f.t1[2] + q * f.t2[2];
}

// ── serialization (single ArrayBuffer, little-endian, byte-stable) ─────────
//
// Layout (binding — see brief):
//   header  Uint32 x12: [version, packCell(regionId), nodeCount, edgeCount,
//                        parcelCount, logCount, totalParcelFloats,
//                        tierPlus1 (0=no settlement, else tier+1), population,
//                        deviation, reserved0, reserved1]
//                        (12 uint32 = 48 bytes, 8-aligned; W2c-1 Task 2 grew this
//                         from 8 to carry the PRIMARY-settlement metadata)
//   frame   Float64 x10: cx, cy, cz, t1[0..2], t2[0..2], radius
//   sections, in fixed order, EACH aligned to an 8-byte boundary by zero pad:
//     nodeU(f64), nodeV(f64), nodeKind(u8),
//     edgeA(u32), edgeB(u32), edgeKind(u8), edgeEpoch(u32),
//     edgeClass(u8), edgeWidth(f32),                      [v11 width channel]
//     parcels: per parcel [u32 polyLen, u32 use, u32 epoch, f64 level,
//                          u8 retaining, f64 poly[polyLen]] (contiguous),
//     logKind(u8), logRef(u32), logSlope(f32).
// Padding bytes are never written, so they stay zero — the buffer is a pure
// function of the graph, hence byte-stable across regenerations.

const HEADER_BYTES = 48; // 12 * Uint32 (8-aligned)
const FRAME_BYTES = 80;  // 10 * Float64

/** Round a byte offset up to the next multiple of 8. */
function align8(n: number): number {
  return (n + 7) & ~7;
}

export function serializeCityGraph(g: CityGraph): ArrayBuffer {
  const civics=g.civics??[];
  const nc = g.nodeKind.length;
  const ec = g.edgeKind.length;
  const pc = g.parcels.length;
  const lc = g.logKind.length;
  let totalParcelFloats = 0;
  for (const p of g.parcels) totalParcelFloats += p.poly.length;

  // Size pass — mirror the write cursor exactly.
  let off = align8(HEADER_BYTES + FRAME_BYTES);
  off = align8(off) + 8 * nc; // nodeU
  off = align8(off) + 8 * nc; // nodeV
  off = align8(off) + nc;     // nodeKind
  off = align8(off) + 4 * ec; // edgeA
  off = align8(off) + 4 * ec; // edgeB
  off = align8(off) + ec;     // edgeKind
  off = align8(off) + 4 * ec; // edgeEpoch
  off = align8(off) + ec;     // edgeClass (v11)
  off = align8(off) + 4 * ec; // edgeWidth (v11)
  off = align8(off);          // parcels section start
  for (const p of g.parcels) off += 21 + 8 * p.poly.length; // 4+4+4+8+1 + poly
  off = align8(off) + lc;     // logKind
  off = align8(off) + 4 * lc; // logRef
  off = align8(off) + 4 * lc; // logSlope
  const total = align8(off)+64*civics.length;

  const buf = new ArrayBuffer(total);
  const dv = new DataView(buf);

  // header
  dv.setUint32(0, g.version, true);
  dv.setUint32(4, packCell(g.regionId), true);
  dv.setUint32(8, nc, true);
  dv.setUint32(12, ec, true);
  dv.setUint32(16, pc, true);
  dv.setUint32(20, lc, true);
  dv.setUint32(24, totalParcelFloats, true);
  // Primary-settlement metadata (W2c-1 Task 2). tier stored +1 so 0 = "none".
  dv.setUint32(28, g.tier < 0 ? 0 : g.tier + 1, true);
  dv.setUint32(32, g.population >>> 0, true);
  dv.setUint32(36, g.deviation >>> 0, true);
  dv.setUint32(40, civics.length, true); // civic count (v20)
  dv.setUint32(44, 0, true); // reserved1

  // frame
  const f = g.frame;
  const frameVals = [f.cx, f.cy, f.cz, f.t1[0], f.t1[1], f.t1[2], f.t2[0], f.t2[1], f.t2[2], f.radius];
  for (let i = 0; i < 10; i++) dv.setFloat64(HEADER_BYTES + i * 8, frameVals[i], true);

  // sections
  let o = align8(HEADER_BYTES + FRAME_BYTES);
  o = align8(o); for (let i = 0; i < nc; i++) { dv.setFloat64(o, g.nodeU[i], true); o += 8; }
  o = align8(o); for (let i = 0; i < nc; i++) { dv.setFloat64(o, g.nodeV[i], true); o += 8; }
  o = align8(o); for (let i = 0; i < nc; i++) { dv.setUint8(o, g.nodeKind[i]); o += 1; }
  o = align8(o); for (let i = 0; i < ec; i++) { dv.setUint32(o, g.edgeA[i], true); o += 4; }
  o = align8(o); for (let i = 0; i < ec; i++) { dv.setUint32(o, g.edgeB[i], true); o += 4; }
  o = align8(o); for (let i = 0; i < ec; i++) { dv.setUint8(o, g.edgeKind[i]); o += 1; }
  o = align8(o); for (let i = 0; i < ec; i++) { dv.setUint32(o, g.edgeEpoch[i], true); o += 4; }
  o = align8(o); for (let i = 0; i < ec; i++) { dv.setUint8(o, g.edgeClass[i]); o += 1; }
  o = align8(o); for (let i = 0; i < ec; i++) { dv.setFloat32(o, g.edgeWidth[i], true); o += 4; }
  o = align8(o);
  for (const p of g.parcels) {
    dv.setUint32(o, p.poly.length, true); o += 4;
    dv.setUint32(o, p.use, true); o += 4;
    dv.setUint32(o, p.epoch, true); o += 4;
    dv.setFloat64(o, p.level, true); o += 8;
    dv.setUint8(o, p.retaining ? 1 : 0); o += 1;
    for (let i = 0; i < p.poly.length; i++) { dv.setFloat64(o, p.poly[i], true); o += 8; }
  }
  o = align8(o); for (let i = 0; i < lc; i++) { dv.setUint8(o, g.logKind[i]); o += 1; }
  o = align8(o); for (let i = 0; i < lc; i++) { dv.setUint32(o, g.logRef[i], true); o += 4; }
  o = align8(o); for (let i = 0; i < lc; i++) { dv.setFloat32(o, g.logSlope[i], true); o += 4; }
  o=align8(o);
  for(const c of civics){
    for(const n of [c.seedK,c.tier,c.admin.parcel,c.ward.parcel]){dv.setUint32(o,n,true);o+=4;}
    for(const n of [c.admin.u,c.admin.v,c.admin.yaw,c.ward.u,c.ward.v,c.ward.yaw]){dv.setFloat64(o,n,true);o+=8;}
  }

  return buf;
}

export function deserializeCityGraph(buf: ArrayBuffer): CityGraph {
  const dv = new DataView(buf);
  const version = dv.getUint32(0, true);
  if (version !== CITYGRAPH_VERSION) {
    throw new Error(`deserializeCityGraph: version mismatch (got ${version}, expected ${CITYGRAPH_VERSION})`);
  }
  const regionId = unpackCell(dv.getUint32(4, true));
  const nc = dv.getUint32(8, true);
  const ec = dv.getUint32(12, true);
  const pc = dv.getUint32(16, true);
  const lc = dv.getUint32(20, true);
  const totalParcelFloats = dv.getUint32(24, true);
  const tierPlus1 = dv.getUint32(28, true);
  const tier = tierPlus1 === 0 ? -1 : tierPlus1 - 1;
  const population = dv.getUint32(32, true);
  const deviation = dv.getUint32(36, true);

  const fv: number[] = [];
  for (let i = 0; i < 10; i++) fv.push(dv.getFloat64(HEADER_BYTES + i * 8, true));
  const frame: RegionFrame = {
    cx: fv[0], cy: fv[1], cz: fv[2],
    t1: [fv[3], fv[4], fv[5]], t2: [fv[6], fv[7], fv[8]],
    radius: fv[9],
  };

  const nodeU = new Float64Array(nc);
  const nodeV = new Float64Array(nc);
  const nodeKind = new Uint8Array(nc);
  const edgeA = new Uint32Array(ec);
  const edgeB = new Uint32Array(ec);
  const edgeKind = new Uint8Array(ec);
  const edgeEpoch = new Uint32Array(ec);
  const edgeClass = new Uint8Array(ec);
  const edgeWidth = new Float32Array(ec);
  const logKind = new Uint8Array(lc);
  const logRef = new Uint32Array(lc);
  const logSlope = new Float32Array(lc);
  const parcels: Parcel[] = [];

  let o = align8(HEADER_BYTES + FRAME_BYTES);
  o = align8(o); for (let i = 0; i < nc; i++) { nodeU[i] = dv.getFloat64(o, true); o += 8; }
  o = align8(o); for (let i = 0; i < nc; i++) { nodeV[i] = dv.getFloat64(o, true); o += 8; }
  o = align8(o); for (let i = 0; i < nc; i++) { nodeKind[i] = dv.getUint8(o); o += 1; }
  o = align8(o); for (let i = 0; i < ec; i++) { edgeA[i] = dv.getUint32(o, true); o += 4; }
  o = align8(o); for (let i = 0; i < ec; i++) { edgeB[i] = dv.getUint32(o, true); o += 4; }
  o = align8(o); for (let i = 0; i < ec; i++) { edgeKind[i] = dv.getUint8(o); o += 1; }
  o = align8(o); for (let i = 0; i < ec; i++) { edgeEpoch[i] = dv.getUint32(o, true); o += 4; }
  o = align8(o); for (let i = 0; i < ec; i++) { edgeClass[i] = dv.getUint8(o); o += 1; }
  o = align8(o); for (let i = 0; i < ec; i++) { edgeWidth[i] = dv.getFloat32(o, true); o += 4; }
  o = align8(o);
  let seenFloats = 0;
  for (let p = 0; p < pc; p++) {
    const polyLen = dv.getUint32(o, true); o += 4;
    const use = dv.getUint32(o, true); o += 4;
    const epoch = dv.getUint32(o, true); o += 4;
    const level = dv.getFloat64(o, true); o += 8;
    const retaining = dv.getUint8(o) !== 0; o += 1;
    const poly = new Float64Array(polyLen);
    for (let i = 0; i < polyLen; i++) { poly[i] = dv.getFloat64(o, true); o += 8; }
    seenFloats += polyLen;
    parcels.push({ poly, use, epoch, level, retaining });
  }
  if (seenFloats !== totalParcelFloats) {
    throw new Error(`deserializeCityGraph: parcel-float count mismatch (got ${seenFloats}, header ${totalParcelFloats})`);
  }
  o = align8(o); for (let i = 0; i < lc; i++) { logKind[i] = dv.getUint8(o); o += 1; }
  o = align8(o); for (let i = 0; i < lc; i++) { logRef[i] = dv.getUint32(o, true); o += 4; }
  o = align8(o); for (let i = 0; i < lc; i++) { logSlope[i] = dv.getFloat32(o, true); o += 4; }
  const civics:SettlementCivicSite[]=[];o=align8(o);
  for(let i=0;i<dv.getUint32(40,true);i++){
    const seedK=dv.getUint32(o,true),tier=dv.getUint32(o+4,true),ap=dv.getUint32(o+8,true),wp=dv.getUint32(o+12,true);o+=16;
    const n:number[]=[];for(let j=0;j<6;j++){n.push(dv.getFloat64(o,true));o+=8;}
    if(tier>5||ap>=pc||wp>=pc||ap===wp||n.some(v=>!Number.isFinite(v))||civics.some(c=>c.seedK===seedK))throw new Error('Invalid settlement civic site');
    civics.push({seedK,tier,admin:{parcel:ap,u:n[0],v:n[1],yaw:n[2]},ward:{parcel:wp,u:n[3],v:n[4],yaw:n[5]}});
  }

  return {
    regionId, frame,
    nodeU, nodeV, nodeKind,
    edgeA, edgeB, edgeKind, edgeEpoch, edgeClass, edgeWidth,
    parcels,
    logKind, logRef, logSlope,
    version,
    tier, population, deviation,...(civics.length?{civics}:{}),
  };
}

/** Byte-for-byte buffer comparison — the determinism assertion later tasks use. */
export function cityGraphBytesEqual(a: ArrayBuffer, b: ArrayBuffer): boolean {
  if (a.byteLength !== b.byteLength) return false;
  const ua = new Uint8Array(a);
  const ub = new Uint8Array(b);
  for (let i = 0; i < ua.length; i++) if (ua[i] !== ub[i]) return false;
  return true;
}

// ── mutable builder (consumed by the SC growth tasks 3–5) ──────────────────

/**
 * Accumulates a region's network during street-cell growth, then freezes into
 * an immutable CityGraph via {@link build}. Appenders return the new element's
 * index so callers can wire edges/parcels/log references without a second pass.
 *
 * Read access to node coordinates ({@link nodeCount}, {@link nodeUAt},
 * {@link nodeVAt}) and the {@link nearestNode} helper support the spatial
 * queries growth performs (snap-to-existing-junction, loop closure).
 */
export class CityGraphBuilder {
  readonly regionId: CellId;
  readonly frame: RegionFrame;

  private _nodeU: number[] = [];
  private _nodeV: number[] = [];
  private _nodeKind: number[] = [];
  private _edgeA: number[] = [];
  private _edgeB: number[] = [];
  private _edgeKind: number[] = [];
  private _edgeEpoch: number[] = [];
  private _edgeClass: number[] = [];
  private _edgeWidth: number[] = [];
  private _parcels: Parcel[] = [];
  private _logKind: number[] = [];
  private _logRef: number[] = [];
  private _logSlope: number[] = [];
  // Primary-settlement metadata (W2c-1 Task 2); defaults = settlement-less cell.
  private _tier = -1;
  private _population = 0;
  private _deviation = 0;

  constructor(radius: number, regionId: CellId) {
    this.regionId = regionId;
    this.frame = regionFrame(radius, regionId);
  }

  /** Append a node at region-local (u,v); returns its index. */
  addNode(u: number, v: number, kind: number): number {
    const i = this._nodeU.length;
    this._nodeU.push(u);
    this._nodeV.push(v);
    this._nodeKind.push(kind);
    return i;
  }

  /**
   * Append an edge between node indices a,b; returns its index. `cls`/`width`
   * (v11) default to UNSET/0 — growth-stage edges are classified + width-resolved
   * by the classify pass; edges created AFTER it (alley cuts, driveway stubs)
   * pass their class + resolved width here directly.
   */
  addEdge(a: number, b: number, kind: number, epoch: number, cls = CLASS_UNSET, width = 0): number {
    const i = this._edgeA.length;
    this._edgeA.push(a);
    this._edgeB.push(b);
    this._edgeKind.push(kind);
    this._edgeEpoch.push(epoch);
    this._edgeClass.push(cls);
    this._edgeWidth.push(width);
    return i;
  }

  /** Current edge count (classify-pass support, v11). */
  get edgeCount(): number { return this._edgeA.length; }
  /** Endpoint A of edge i. */ edgeAAt(i: number): number { return this._edgeA[i]; }
  /** Endpoint B of edge i. */ edgeBAt(i: number): number { return this._edgeB[i]; }
  /** Kind of edge i. */ edgeKindAt(i: number): number { return this._edgeKind[i]; }
  /** Epoch of edge i. */ edgeEpochAt(i: number): number { return this._edgeEpoch[i]; }
  /** Width class of edge i (CLASS_*). */ edgeClassAt(i: number): number { return this._edgeClass[i]; }
  /** Resolved width of edge i (metres). */ edgeWidthAt(i: number): number { return this._edgeWidth[i]; }
  /** Stamp edge i's width class + resolved width (the classify pass, v11). */
  setEdgeClass(i: number, cls: number, width: number): void {
    this._edgeClass[i] = cls;
    this._edgeWidth[i] = width;
  }

  /** Append a growth-log entry (its index is the growth step). */
  log(kind: number, ref: number, slope: number): void {
    this._logKind.push(kind);
    this._logRef.push(ref);
    this._logSlope.push(slope);
  }

  /** Append a parcel; returns its index. */
  addParcel(p: Parcel): number {
    const i = this._parcels.length;
    this._parcels.push(p);
    return i;
  }

  /** Current parcel count (placement support, W2c-1 Task 2). */
  get parcelCount(): number { return this._parcels.length; }
  /** Parcel `i`'s polygon (placement reads centroids to rank by seed distance). */
  parcelPolyAt(i: number): Float64Array { return this._parcels[i].poly; }
  /** Set parcel `i`'s USE_* code (required-building placement). */
  setParcelUse(i: number, use: number): void { this._parcels[i].use = use; }
  /** Stamp the region's primary-settlement metadata (serialized in the header). */
  setMeta(tier: number, population: number, deviation: number): void {
    this._tier = tier; this._population = population; this._deviation = deviation;
  }

  /** Current node count (spatial-query support). */
  get nodeCount(): number { return this._nodeU.length; }
  /**
   * Current growth-log length — i.e. the epoch the NEXT logged edge will carry
   * (Task 6). The growth epoch scheme is "log length at edge creation"; a bare
   * builder returns 0, so a first settlement grown on it keeps epochs 0,1,2,…
   * unchanged (Task 4's `edgeEpoch[i]===i` still holds). growSettlement seeds
   * its local epoch counter from THIS value instead of a hard 0, so a SECOND
   * settlement grown onto the same builder continues the counter rather than
   * colliding at 0 — keeping edge epochs globally monotone across settlements
   * (the multi-settlement fix owed since Task 4, resolved by materializeRegion).
   */
  get logCount(): number { return this._logKind.length; }
  /** Local u of node i. */
  nodeUAt(i: number): number { return this._nodeU[i]; }
  /** Local v of node i. */
  nodeVAt(i: number): number { return this._nodeV[i]; }
  /** Kind of node i. */
  nodeKindAt(i: number): number { return this._nodeKind[i]; }

  /**
   * Index of the node nearest to (u,v) in region-local meters, or -1 if none
   * (optionally within `maxDist` meters). Linear scan — growth regions hold at
   * most a few thousand nodes.
   *
   * TIE-BREAK NOTE: the `d <= bestD` comparison keeps the HIGHEST index on a tie
   * (last equal-distance node wins), which DIFFERS from growth's NodeHash.nearest
   * convention (`d < bestD || (d === bestD && idx < best)` → LOWEST index wins).
   * Both are deterministic; the two callers never mix, so do NOT change either
   * behavior to "align" them (it would shift the growth digest for no gain).
   */
  nearestNode(u: number, v: number, maxDist = Infinity): number {
    let best = -1;
    let bestD = maxDist;
    for (let i = 0; i < this._nodeU.length; i++) {
      const d = pmath.hypot(this._nodeU[i] - u, this._nodeV[i] - v);
      if (d <= bestD) { bestD = d; best = i; }
    }
    return best;
  }

  /** Freeze into an immutable CityGraph with flat typed arrays. */
  build(): CityGraph {
    return {
      regionId: this.regionId,
      frame: this.frame,
      nodeU: Float64Array.from(this._nodeU),
      nodeV: Float64Array.from(this._nodeV),
      nodeKind: Uint8Array.from(this._nodeKind),
      edgeA: Uint32Array.from(this._edgeA),
      edgeB: Uint32Array.from(this._edgeB),
      edgeKind: Uint8Array.from(this._edgeKind),
      edgeEpoch: Uint32Array.from(this._edgeEpoch),
      edgeClass: Uint8Array.from(this._edgeClass),
      edgeWidth: Float32Array.from(this._edgeWidth),
      parcels: this._parcels.slice(),
      logKind: Uint8Array.from(this._logKind),
      logRef: Uint32Array.from(this._logRef),
      logSlope: Float32Array.from(this._logSlope),
      version: CITYGRAPH_VERSION,
      tier: this._tier, population: this._population, deviation: this._deviation,
    };
  }
}
